import React, { useEffect, useMemo, useState } from "react";

import type { ClientId } from "../../domain/types";
import { persistAnswer, useCoAnswer, useCoClient } from "../../data/store";
import {
  dbTrue,
  fetchZohoRecords,
  refreshZohoDataset,
  type ZohoDatasetReadDTO,
  type ZohoLedgerDTO,
} from "../../data/zohoRecordsApi";
import { GROUP_LABEL, GROUP_ORDER, groupForType, type LedgerGroup } from "../../domain/ledgers";
import { Callout } from "../../components/Callout";
import { DatasetFreshness, DatasetReadError } from "../../components/DatasetFreshness";
import { DecisionCardOption } from "../../components/DecisionCards";
import { QuestionCard } from "../../components/QuestionCard";
import { SubStepShell } from "../../components/SubStepShell";
import { cn } from "../../components/cn";
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  SearchIcon,
} from "../../components/icons";
import { LedgerImportPanel } from "../ledger-import/LedgerImportPanel";
import { NoBillMappingPanel } from "../no-bill/NoBillMappingPanel";
import { NoBillPanel } from "../no-bill/NoBillPanel";
import { useSubStepNav } from "../useSubStepNav";

const NO_BILLS_ANSWER_KEY = "verifySetup.noBillsPolicy";

/**
 * The recorded decision. The ledgers themselves live in Zoho, not here — see
 * NoBillPanel, and `NoBillLedgerService` behind it.
 *
 * `perBillLimit` is a policy figure with no enforcement anywhere yet. It belongs
 * on the setup certificate at the end of onboarding; until that screen exists it
 * is written down and read by nobody, which is worth knowing before relying on it.
 */
interface NoBillsPolicy {
  choice?: "no-bills-ledger" | "direct-to-expense" | "map-existing";
  perBillLimit?: string;
}

/**
 * Three answers, and they are three different operations rather than three
 * wordings of one.
 *
 * The first writes accounts to the client's Zoho Books. The third writes only to
 * our database — a client who has been doing this for years under their own names
 * needs nothing created, just a record of which ledgers those are. The second
 * writes nothing anywhere.
 *
 * One at a time, deliberately. A client with an existing catch-all who also wants
 * new categories fits two of these at once, and that case has not come up yet —
 * designing for it now would mean guessing at how the two should combine. Noted
 * in docs/onboarding-open-items.md rather than half-built.
 */
const NO_BILLS_OPTIONS: DecisionCardOption[] = [
  {
    value: "no-bills-ledger",
    title: 'Create a "No Bills" ledger',
    description:
      "We add No Bill - Expense to their books, with a ledger under it for each kind of spend that arrives without one.",
  },
  {
    value: "map-existing",
    title: "They already have ledgers for this",
    description:
      "Point at the ledgers they already use. We record which ones they are and change nothing in their books.",
  },
  {
    value: "direct-to-expense",
    // Nothing is created in Zoho for this answer. Adding the folder anyway would
    // make the choices indistinguishable in the books, which is the whole point
    // of asking, and leave an account nobody posts to for somebody to explain
    // later.
    title: "Post directly to expense ledgers",
    description: "Spend without a bill goes to the same expense ledgers as everything else.",
  },
];

/**
 * What CAs set the per-bill cap to, and why.
 *
 * Cash payments above ₹10,000 to one person in one day are disallowed under
 * section 40A(3) — ₹35,000 for goods transport — so that is the figure the
 * profession reaches for. A default with a reason beside it is more useful than
 * an empty box, and the CA can still type anything.
 */
const PER_BILL_LIMIT_DEFAULT = "10000";

/**
 * One parent and the children Zoho hung off it.
 *
 * Built from `parent_account_id` rather than from indentation, so a child whose
 * parent sits in a different group still finds it. Any child we cannot place
 * (parent inactive and filtered out upstream, say) is promoted to a root, which
 * is the only outcome that keeps it on the screen.
 */
interface LedgerNode {
  ledger: ZohoLedgerDTO;
  children: ZohoLedgerDTO[];
}

function toTree(ledgers: ZohoLedgerDTO[]): LedgerNode[] {
  const nodes = new Map<string, LedgerNode>();
  for (const ledger of ledgers) {
    if (ledger.parent_account_id === null) nodes.set(ledger.account_id, { ledger, children: [] });
  }

  const roots: LedgerNode[] = [];
  for (const ledger of ledgers) {
    if (ledger.parent_account_id === null) {
      roots.push(nodes.get(ledger.account_id)!);
      continue;
    }
    const parent = nodes.get(ledger.parent_account_id);
    if (parent) parent.children.push(ledger);
    else roots.push({ ledger, children: [] });
  }

  return roots;
}

function matches(ledger: ZohoLedgerDTO, query: string): boolean {
  const name = (ledger.account_name ?? "").toLowerCase();
  const code = (ledger.account_code ?? "").toLowerCase();
  return name.includes(query) || code.includes(query);
}

// A parent survives the search if it matches or any of its children do, and a
// matching child keeps its parent as context. Dropping the parent would leave
// "Input CGST" floating with nothing to say what it hangs off.
function filterTree(nodes: LedgerNode[], query: string): LedgerNode[] {
  const out: LedgerNode[] = [];
  for (const node of nodes) {
    if (matches(node.ledger, query)) {
      out.push(node);
      continue;
    }
    const children = node.children.filter((child) => matches(child, query));
    if (children.length > 0) out.push({ ledger: node.ledger, children });
  }

  return out;
}

// ── Rows ────────────────────────────────────────────────────────────────────

/**
 * One account. The tick means "read from the books", not "checked by a
 * reviewer" — `confirmed_at` is the column that will carry the second meaning,
 * and this screen does not set it yet.
 */
function LedgerRow({ ledger, nested }: { ledger: ZohoLedgerDTO; nested?: boolean }) {
  return (
    <li className={cn("flex items-center gap-1.5 text-sm", nested && "pl-5")}>
      <CheckIcon className="size-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
      <span className="truncate">{ledger.account_name}</span>
      {ledger.account_code && (
        <span className="shrink-0 text-xs text-muted-foreground">{ledger.account_code}</span>
      )}
      {!dbTrue(ledger.is_active) && (
        <span className="shrink-0 rounded-sm bg-muted px-1 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground/80">
          inactive
        </span>
      )}
    </li>
  );
}

function LedgerGroupSection({
  group,
  total,
  nodes,
  open,
  onToggle,
}: {
  group: LedgerGroup;
  total: number;
  nodes: LedgerNode[];
  open: boolean;
  onToggle: () => void;
}) {
  const Chevron = open ? ChevronDownIcon : ChevronRightIcon;

  return (
    <li className="flex flex-col py-2 first:pt-0 last:pb-0">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex items-center gap-1.5 py-1 text-left text-sm font-medium"
      >
        <Chevron className="size-4 text-muted-foreground" />
        {GROUP_LABEL[group]} ({total})
      </button>
      {open && (
        // CSS columns rather than a grid: a parent and its children are one
        // block that `break-inside-avoid` keeps together, and the list reads
        // down each column instead of zig-zagging across a row.
        <ul className="pb-1 pl-6 pt-1 sm:columns-2 sm:gap-6">
          {nodes.map((node) => (
            <li key={node.ledger.account_id} className="mb-1 break-inside-avoid">
              <ul className="flex flex-col gap-1">
                <LedgerRow ledger={node.ledger} />
                {node.children.map((child) => (
                  <LedgerRow key={child.account_id} ledger={child} nested />
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

// ── Step ────────────────────────────────────────────────────────────────────

export function LedgersStep({ clientId }: { clientId: ClientId }) {
  const client = useCoClient(clientId);
  const { goNext } = useSubStepNav(clientId, "ledgers");
  const noBillsPolicy = useCoAnswer<NoBillsPolicy>(clientId, NO_BILLS_ANSWER_KEY);

  const [ledgers, setLedgers] = useState<ZohoLedgerDTO[] | null>(null);
  const [readback, setReadback] = useState<ZohoDatasetReadDTO | null>(null);
  // Only so the push confirm can name the books it is about to write to. Zoho's
  // own name for the organisation, not ours for the client — those differ often
  // enough that showing the wrong one would undermine the whole point of asking.
  const [organisationName, setOrganisationName] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  /**
   * Every group starts closed.
   *
   * An ordinary chart is eighty-odd accounts, and open by default that is a wall
   * of names between the top of the step and everything the CA has come here to
   * do. The counts on the headers — "Expenses (39)" — are what most visits need,
   * and searching forces the groups open anyway, so nothing is hidden from
   * somebody looking for a particular account.
   */
  const [collapsed, setCollapsed] = useState<Set<LedgerGroup>>(() => new Set(GROUP_ORDER));

  useEffect(() => {
    let live = true;
    setLoadError(null);

    fetchZohoRecords(clientId, ["ledgers", "organisation"])
      .then((records) => {
        if (!live) return;
        setLedgers(records.ledgers ?? []);
        setReadback(records.readbacks?.ledgers ?? null);
        setOrganisationName(records.organisation?.name ?? null);
      })
      .catch((error: unknown) => {
        if (live) setLoadError(error instanceof Error ? error.message : "Could not load the chart of accounts.");
      });

    return () => {
      live = false;
    };
  }, [clientId]);

  /**
   * Pull the chart of accounts out of Zoho again.
   *
   * Deliberately not on a timer and not on mount. It is a real Zoho call and
   * the answer changes rarely, so it belongs on a button somebody presses when
   * they have a reason to — most often just before editing against it.
   *
   * A read that Zoho refuses still resolves: the reason comes back on the
   * readback and renders where the freshness line is, so the ledgers already on
   * screen stay put instead of the page emptying itself over a bad minute.
   */
  function refresh() {
    setRefreshing(true);

    refreshZohoDataset(clientId, "ledgers")
      .then((records) => {
        setLedgers(records.ledgers ?? []);
        setReadback(records.readbacks?.ledgers ?? null);
      })
      .catch((error: unknown) => {
        setLoadError(error instanceof Error ? error.message : "Could not re-read the chart of accounts.");
      })
      .finally(() => setRefreshing(false));
  }

  /**
   * Re-read the chart after a push added to it.
   *
   * Our own copy, not Zoho's — the push already re-read from Zoho as its last
   * act, so going again here would be a second trip for the same answer. This
   * only picks up what that left behind.
   */
  function reloadLedgers() {
    fetchZohoRecords(clientId, ["ledgers"])
      .then((records) => {
        setLedgers(records.ledgers ?? []);
        setReadback(records.readbacks?.ledgers ?? null);
      })
      .catch(() => {
        // The push worked and said so; failing to redraw the list above it is
        // not worth an error banner over the top of that. Refresh is right there.
      });
  }

  const query = search.trim().toLowerCase();

  const groups = useMemo(() => {
    if (ledgers === null) return [];

    return GROUP_ORDER.map((group) => {
      const inGroup = ledgers.filter((ledger) => groupForType(ledger.account_type) === group);
      const tree = toTree(inGroup);
      return {
        group,
        total: inGroup.length,
        nodes: query === "" ? tree : filterTree(tree, query),
      };
    }).filter((entry) => entry.total > 0 && (query === "" || entry.nodes.length > 0));
  }, [ledgers, query]);

  if (!client) return null;

  function toggleGroup(group: LedgerGroup) {
    setCollapsed((previous) => {
      const next = new Set(previous);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  }

  function selectNoBillsChoice(value: string) {
    const choice = value as NoBillsPolicy["choice"];

    void persistAnswer<NoBillsPolicy>(clientId, NO_BILLS_ANSWER_KEY, {
      ...noBillsPolicy,
      choice,
      // A real default rather than a greyed-out hint, and only on the answer it
      // belongs to: an empty box reads as "no limit", which is not what any firm
      // means. Typed over freely, and left alone once the CA has typed anything.
      perBillLimit:
        choice !== "direct-to-expense" && (noBillsPolicy?.perBillLimit ?? "") === ""
          ? PER_BILL_LIMIT_DEFAULT
          : noBillsPolicy?.perBillLimit,
    });
  }

  const clientName = client.name || "this client";
  const readCount = ledgers?.length ?? 0;
  const choice = noBillsPolicy?.choice;
  // The limit is a policy about undocumented spend, so it belongs to both answers
  // that keep it somewhere of its own — created by us or already theirs.
  const showLimit = choice === "no-bills-ledger" || choice === "map-existing";
  // The one decision this screen asks for. The limit auto-fills on selection,
  // so it only blocks if the CA cleared the box deliberately.
  const ready = choice !== undefined && (!showLimit || (noBillsPolicy?.perBillLimit ?? "").trim() !== "");

  return (
    <SubStepShell
      title="Set Up Your Chart of Accounts"
      description={`Below are the current Chart of Accounts available in ${clientName}'s Zoho Books account.`}
      thenWhat={ready ? "Continue → Configure rounding off." : "Answer how expenses without bills are handled to continue."}
      onContinue={() => void goNext()}
      continueDisabled={!ready}
    >
      <section className="flex flex-col gap-3 rounded-lg border p-4">
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
          <h3 className="text-base font-medium">
            Current ledgers
            {ledgers !== null && (
              <span className="ml-2 text-xs font-normal text-muted-foreground">{readCount} total</span>
            )}
          </h3>
          {ledgers !== null && (
            <DatasetFreshness readback={readback} busy={refreshing} onRefresh={refresh} />
          )}
        </div>

        <DatasetReadError readback={readback} />

        {loadError !== null ? (
          <Callout variant="warning">{loadError}</Callout>
        ) : ledgers === null ? (
          <p className="text-sm text-muted-foreground">Reading the chart of accounts…</p>
        ) : readCount === 0 ? (
          <Callout variant="warning">
            No ledgers were read from {clientName}'s books. Go back to Confirm Connection to Zoho and re-run the
            read if the connection is live.
          </Callout>
        ) : (
          <>
            <label className="relative flex items-center">
              <SearchIcon className="pointer-events-none absolute left-2.5 size-4 text-muted-foreground" />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search ledgers"
                className="h-9 w-full rounded-md border bg-background pl-8 pr-3 text-sm"
              />
            </label>

            {groups.length === 0 ? (
              <p className="text-sm text-muted-foreground">No ledgers match "{search.trim()}".</p>
            ) : (
              <ul className="flex flex-col divide-y">
                {groups.map((entry) => (
                  <LedgerGroupSection
                    key={entry.group}
                    group={entry.group}
                    total={entry.total}
                    nodes={entry.nodes}
                    // Searching forces every group open: a hit hidden inside a
                    // collapsed group reads as "no results".
                    open={query !== "" || !collapsed.has(entry.group)}
                    onToggle={() => toggleGroup(entry.group)}
                  />
                ))}
              </ul>
            )}
          </>
        )}
      </section>

      {/*
        The download is the template: the sheet arrives holding the chart above,
        every row carrying its Zoho id, so an edit is unambiguous and an
        addition is a row with that cell left blank. Upload parses and diffs it
        locally; the push behind the confirm is the only thing on this screen
        that writes to the client's books.
      */}

      {/*
        Said here as well as in the sheet's own refusal, because by the time the
        upload refuses a row the CA has already typed the account. Banks &
        Credit Cards is a later sub-step in this same section, so this is a
        "that comes later" rather than a "go back" — and not a link, because a
        forward slug is neither done nor the frontier and OnboardingStepPage
        would bounce it straight back here.
      */}
      <Callout variant="info">
        Bank and credit card accounts are added on the{" "}
        <span className="font-medium text-foreground">Banks &amp; Credit Cards</span> step, later in
        this section — not on this sheet. Each one needs a currency, which Zoho fixes when the
        account is created and will not change afterwards, so the Chart of Accounts dropdown leaves
        them out. Bank ledgers already in the client's books stay in the sheet and can be edited
        here as usual.
      </Callout>

      <LedgerImportPanel
        clientId={clientId}
        clientName={clientName}
        organisationName={organisationName}
        onPushed={reloadLedgers}
      />


      <section className="flex flex-col gap-3 border-t pt-4">
        <QuestionCard
          headline="How should we account for expenses that do not have bills?"
          whyLine="In Zournal, expenses should generally have a bill. However, we understand that there may be smaller expenses for which a bill is not available. Please let us know how you would like these expenses to be accounted for."
          options={NO_BILLS_OPTIONS}
          selected={noBillsPolicy?.choice}
          onSelect={selectNoBillsChoice}
        />

        {showLimit && (
          <div className="flex flex-col gap-4">
            {choice === "no-bills-ledger" ? (
              <NoBillPanel
                clientId={clientId}
                clientName={clientName}
                organisationName={organisationName}
                onPushed={reloadLedgers}
              />
            ) : (
              <NoBillMappingPanel clientId={clientId} clientName={clientName} />
            )}

            <label className="flex flex-col gap-1 text-sm sm:max-w-xs">
              <span className="text-xs font-medium text-muted-foreground">
                Maximum amount per bill posted to No Bills
              </span>
              <input
                type="number"
                value={noBillsPolicy?.perBillLimit ?? ""}
                onChange={(event) =>
                  void persistAnswer<NoBillsPolicy>(clientId, NO_BILLS_ANSWER_KEY, {
                    ...noBillsPolicy,
                    perBillLimit: event.target.value,
                  })
                }
                placeholder={PER_BILL_LIMIT_DEFAULT}
                className="h-9 rounded-md border bg-background px-3 text-sm"
              />
              {/* The reason without the citation. A CA already knows 40A(3); what
                  the line is for is explaining where 10,000 came from, and "not an
                  allowed expense" is the part that matters to anyone who does not. */}
              <span className="text-xs text-muted-foreground">
                Usually ₹10,000. Cash above that to one person in a day is not an allowed expense.
              </span>
            </label>
          </div>
        )}
      </section>
    </SubStepShell>
  );
}
